/**
 * OPENING A FILED DOCUMENT WITHOUT STRANDING HIM (MOB4, the 128th).
 *
 * His phone report, 2026-10-09: a filed Word file from a property's documents opened a white screen he
 * could not leave. Reproduced in the real app installed as an iPhone home-screen app, on his own LOI.
 * What was measured there, and in the harness beside it (`docs/planning/2026-10-09-mob1-repro/`):
 *
 *   1. A link SIGNED FIRST and opened after it: at iPhone's default the window never opens (the API
 *      answered 200, the page did not change - a dead button); with pop-ups allowed iOS asks first.
 *   2. A window opened INSIDE the tap opens either way, and iOS gives a window pointed at a file its
 *      own sheet with a close. A window the app closes itself returns him to the app in 3 ms.
 *   3. A WORD file in any window is a white page whose loading bar never moves - served inline or as a
 *      download alike. So a window is for a PDF; a Word file opens in the app (`DocumentViewer`).
 *   4. The share sheet takes a Word file (Save to Files, Copy, Mail ...) and never leaves the app -
 *      but it too opens only inside a tap, so the file has to be in hand before he presses.
 */

/**
 * Opens a document whose link has to be signed first: the window NOW, the link when it arrives.
 *
 * ⛔ CALL IT INSIDE THE TAP, with nothing awaited first - the same rule as `printInOwnWindow`. The
 *   signing is handed in as a function precisely so the caller cannot await it on the way here.
 * ⛔ No `noopener` on the first open: with it the browser answers null and there is no window to
 *   point. The opener is cut by hand instead, before the window leaves this origin.
 *
 * Rejects when the link cannot be signed - after closing the window it opened, so a failure never
 * leaves him looking at a blank page.
 */
export const openSignedInTap = async (sign: () => Promise<string>): Promise<void> => {
  const w = window.open('', '_blank');
  let url: string;
  try {
    url = await sign();
    if (!url) throw new Error('Could not open the document');
  } catch (e) {
    w?.close();
    throw e;
  }
  if (w) {
    try { w.opener = null; } catch { /* a browser that will not let go of it still opens the file */ }
    w.location.href = url;
    return;
  }
  // The browser refused the blank window (a desktop pop-up blocker). The real link is worth one try.
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * True on a touch screen whose browser can share files at all - the cheap question, asked before
 * any bytes are fetched. `(pointer: coarse)` rather than a user-agent sniff: at a desk a download is
 * what a mouse expects, and Safari on a Mac can share files too.
 */
export const shareSheetAvailable = (): boolean => {
  const nav = navigator as Navigator & { canShare?: unknown };
  return (
    typeof nav.share === 'function' &&
    typeof nav.canShare === 'function' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
};

/** True when THIS file can go to the share sheet here. A browser may refuse a type it shares others of. */
export const canShareFile = (file: File): boolean => {
  if (!shareSheetAvailable()) return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
};

/**
 * Hands the file to the phone's share sheet. ⛔ Inside a tap, like a window.
 *
 * ⭐ Closing the sheet is HIS CHOICE, not a failure: iOS rejects with `AbortError` when he swipes it
 * away (measured), and reporting that as an error would scold him for changing his mind.
 */
export const shareFile = async (file: File): Promise<'shared' | 'dismissed'> => {
  try {
    await navigator.share({ files: [file] });
    return 'shared';
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return 'dismissed';
    throw e;
  }
};
